import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Clock, Edit2, Plus, RefreshCw, StickyNote, Tag, Trash2 } from 'lucide-react'
import type { CaseScopeNote, ScopeNoteTag } from '../../../types/case-container'
import { VALID_SCOPE_NOTE_TAGS } from '../../../types/case-container'
import { TAG_COLOURS } from './case-scope-tab.constants'

interface NoteFormProps {
  noteContent: string
  setNoteContent: (v: string) => void
  noteTags: ScopeNoteTag[]
  toggleTag: (tag: ScopeNoteTag) => void
  editingNoteId: string | null
  savingNote: boolean
  onCancel: () => void
  onSubmit: () => void
  t: TFunction<'cases'>
}

export function NoteForm(props: NoteFormProps): ReactElement {
  const {
    noteContent,
    setNoteContent,
    noteTags,
    toggleTag,
    editingNoteId,
    savingNote,
    onCancel,
    onSubmit,
    t,
  } = props
  return (
    <div className="border-b border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="space-y-3">
        <textarea
          value={noteContent}
          onChange={(e) => {
            setNoteContent(e.target.value)
          }}
          className="h-24 w-full resize-none rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:ring-2 focus:ring-[#4e5e82] focus:outline-none dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
          placeholder={t('scope.notePlaceholder')}
        />
        <NoteTagSelector noteTags={noteTags} toggleTag={toggleTag} t={t} />
        <NoteFormActions
          editingNoteId={editingNoteId}
          savingNote={savingNote}
          disabled={savingNote || !noteContent.trim()}
          onCancel={onCancel}
          onSubmit={onSubmit}
          t={t}
        />
      </div>
    </div>
  )
}

function NoteTagSelector({
  noteTags,
  toggleTag,
  t,
}: {
  noteTags: ScopeNoteTag[]
  toggleTag: (tag: ScopeNoteTag) => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div>
      <label className="mb-2 block text-xs font-medium text-slate-600 dark:text-slate-400">
        {t('scope.tags')}
      </label>
      <div className="flex flex-wrap gap-2">
        {VALID_SCOPE_NOTE_TAGS.map((tag) => (
          <button
            key={tag}
            onClick={() => {
              toggleTag(tag)
            }}
            className={`rounded-md px-2 py-1 text-xs transition-colors ${
              noteTags.includes(tag)
                ? TAG_COLOURS[tag]
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200 dark:bg-slate-700 dark:text-slate-400 dark:hover:bg-slate-600'
            }`}
          >
            {tag}
          </button>
        ))}
      </div>
    </div>
  )
}

interface NoteFormActionsProps {
  editingNoteId: string | null
  savingNote: boolean
  disabled: boolean
  onCancel: () => void
  onSubmit: () => void
  t: TFunction<'cases'>
}

function NoteFormActions({
  editingNoteId,
  savingNote,
  disabled,
  onCancel,
  onSubmit,
  t,
}: NoteFormActionsProps): ReactElement {
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={onCancel}
        className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-100"
      >
        {t('actions.cancel')}
      </button>
      <button
        onClick={onSubmit}
        disabled={disabled}
        className="inline-flex items-center gap-1.5 rounded-md bg-[#4e5e82] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#5c6e94] disabled:opacity-50"
      >
        {savingNote && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
        {editingNoteId ? t('scope.updateNote') : t('scope.addNote')}
      </button>
    </div>
  )
}

interface NotesSectionProps {
  notes: CaseScopeNote[]
  notesTotal: number
  loadingNotes: boolean
  showNoteForm: boolean
  noteFormProps: Omit<NoteFormProps, 't'>
  onAddNote: () => void
  onEditNote: (note: CaseScopeNote) => void
  onDeleteNote: (id: string) => void
  t: TFunction<'cases'>
}

export function NotesSection(props: NotesSectionProps): ReactElement {
  const {
    notes,
    notesTotal,
    loadingNotes,
    showNoteForm,
    noteFormProps,
    onAddNote,
    onEditNote,
    onDeleteNote,
    t,
  } = props
  return (
    <div className="rounded-lg border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
      <NotesSectionHeader notesTotal={notesTotal} onAddNote={onAddNote} t={t} />
      {showNoteForm && <NoteForm {...noteFormProps} t={t} />}
      <NotesList
        notes={notes}
        loadingNotes={loadingNotes}
        onEditNote={onEditNote}
        onDeleteNote={onDeleteNote}
        t={t}
      />
    </div>
  )
}

function NotesSectionHeader({
  notesTotal,
  onAddNote,
  t,
}: {
  notesTotal: number
  onAddNote: () => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-slate-700">
      <h3 className="flex items-center gap-2 font-medium text-slate-900 dark:text-slate-100">
        <StickyNote className="h-4 w-4" />
        {t('scope.investigationNotes')}
        <span className="text-sm font-normal text-slate-500 dark:text-slate-400">
          ({notesTotal})
        </span>
      </h3>
      <button
        onClick={onAddNote}
        className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <Plus className="h-3.5 w-3.5" />
        {t('scope.addNote')}
      </button>
    </div>
  )
}

interface NotesListProps {
  notes: CaseScopeNote[]
  loadingNotes: boolean
  onEditNote: (note: CaseScopeNote) => void
  onDeleteNote: (id: string) => void
  t: TFunction<'cases'>
}

function NotesList({
  notes,
  loadingNotes,
  onEditNote,
  onDeleteNote,
  t,
}: NotesListProps): ReactElement {
  if (loadingNotes) {
    return (
      <div className="flex items-center justify-center p-4 py-8">
        <RefreshCw className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    )
  }
  if (notes.length === 0) {
    return (
      <div className="p-4 py-8 text-center">
        <StickyNote className="mx-auto h-10 w-10 text-slate-300 dark:text-slate-600" />
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">{t('scope.noNotesYet')}</p>
        <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{t('scope.noNotesHint')}</p>
      </div>
    )
  }
  return (
    <div className="space-y-3 p-4">
      {notes.map((note) => (
        <NoteRow
          key={note.id}
          note={note}
          onEditNote={onEditNote}
          onDeleteNote={onDeleteNote}
          t={t}
        />
      ))}
    </div>
  )
}

function NoteRow({
  note,
  onEditNote,
  onDeleteNote,
  t,
}: {
  note: CaseScopeNote
  onEditNote: (note: CaseScopeNote) => void
  onDeleteNote: (id: string) => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <NoteMetaRow note={note} t={t} />
          <p className="text-sm whitespace-pre-wrap text-slate-700 dark:text-slate-300">
            {note.content}
          </p>
          {note.tags.length > 0 && <NoteTagsRow tags={note.tags as ScopeNoteTag[]} />}
        </div>
        <NoteRowActions note={note} onEditNote={onEditNote} onDeleteNote={onDeleteNote} t={t} />
      </div>
    </div>
  )
}

function NoteMetaRow({ note, t }: { note: CaseScopeNote; t: TFunction<'cases'> }): ReactElement {
  return (
    <div className="mb-2 flex items-center gap-2">
      <Clock className="h-3.5 w-3.5 text-slate-400" />
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {new Date(note.createdAt).toLocaleString()}
      </span>
      {note.updatedAt && (
        <span className="text-xs text-slate-400 dark:text-slate-500">{t('scope.edited')}</span>
      )}
    </div>
  )
}

function NoteTagsRow({ tags }: { tags: ScopeNoteTag[] }): ReactElement {
  return (
    <div className="mt-2 flex items-center gap-2">
      <Tag className="h-3.5 w-3.5 text-slate-400" />
      <div className="flex gap-1">
        {tags.map((tag) => (
          <span
            key={tag}
            className={`rounded px-1.5 py-0.5 text-xs ${TAG_COLOURS[tag] || 'bg-slate-100 text-slate-600'}`}
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  )
}

function NoteRowActions({
  note,
  onEditNote,
  onDeleteNote,
  t,
}: {
  note: CaseScopeNote
  onEditNote: (note: CaseScopeNote) => void
  onDeleteNote: (id: string) => void
  t: TFunction<'cases'>
}): ReactElement {
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => {
          onEditNote(note)
        }}
        className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        title={t('scope.editNote')}
      >
        <Edit2 className="h-3.5 w-3.5" />
      </button>
      <button
        onClick={() => {
          onDeleteNote(note.id)
        }}
        className="p-1 text-slate-400 hover:text-red-600 dark:hover:text-red-400"
        title={t('scope.deleteNote')}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
